import {
  Box,
  Card,
  CircularProgress,
  debounce,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, { useMemo, useState } from "react";
import { useEffect } from "react";
import { Link, useQueryWithStore } from "react-admin";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import resourceNames from "../../../../common/data/resourceNames";
import { useHistory } from "react-router-dom";
import { from } from "manipula";
import { useSearchFields } from "../../../../common/hooks/useSearchFields";
import classnames from "classnames";

const useStyles = makeStyles((theme: Theme) => ({
  breadcrumbViewContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    alignItems: "center",
  },
  dropdownTypography: {
    padding: theme.spacing(2),
  },
  searchField: {
    width: "300px",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: "8px",
  },
  anchorDiv: {
    minHeight: "48px",
    minWidth: "48px",
  },
  popupContainer: {
    overflow: "hidden",
  },
  dropdownResults: {
    overflowY: "auto",
    maxHeight: "75vh",
  },
  marginBottom4: {
    marginBottom: "4px",
  },
}));

export interface SelectedDriver {
  id?: number | null;
  fullName?: string | null;
  driverId?: string | null;
}

interface Props {
  driver: SelectedDriver;
  getBasePath: (driver: SelectedDriver) => string;
  getLink?: (driver: SelectedDriver) => string;
}

interface DriverNameDisplayProps {
  fullName: string;
  driverId: string;
}

interface DriverNameBreadcrumbViewProps {
  children: React.ReactNode;
  getBasePath: (driver: SelectedDriver) => string;
  selectedDriver: SelectedDriver;
}

const DriverNameDisplay = ({ fullName, driverId }: DriverNameDisplayProps) => (
  <Box display="flex" flexDirection="row" gridGap={5}>
    <sup>
      <Typography>{driverId}</Typography>
    </sup>
    <Typography variant="h6">{fullName}</Typography>
  </Box>
);

const DriverNameBreadcrumbView = ({
  children,
  getBasePath,
  selectedDriver,
}: DriverNameBreadcrumbViewProps) => {
  const [driversList, setDriversList] = useState<SelectedDriver[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const debounceSetSearchText = useMemo(
    () =>
      debounce((nextValue: string) => {
        setDebouncedSearchText(nextValue);
      }, 500),
    []
  );

  useSearchFields({}, ["driverName"], resourceNames.ADMIN_DRIVERS_VIEW);
  const { data, loading, total } = useQueryWithStore({
    type: "getList",
    resource: resourceNames.ADMIN_DRIVERS_VIEW,
    payload: {
      sort: {
        field: "driverName",
        order: "ASC",
      },
      filter: {
        q: debouncedSearchText,
        status: "Active",
      },
      pagination: {
        page: 1,
        perPage: 1000,
      },
    },
  });

  useEffect(() => {
    if (!data || !total) {
      return;
    }

    const list: SelectedDriver[] = from(
      Object.getOwnPropertyNames(data).map((x) => ({
        id: data[x].id,
        fullName: data[x].driverName,
        driverId: data[x].driverId,
      }))
    )
      .where((x) => x.id)
      .orderBy((x) => x.fullName)
      .toArray();
    setDriversList(list);
  }, [data, total]);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDriverSelected = (driver: SelectedDriver) => {
    handleClose();
    if (getBasePath && selectedDriver.id !== driver.id) {
      history.push(getBasePath(driver));
    }
  };

  const classes = useStyles();
  const dropdownOpen = Boolean(anchorEl);
  return (
    <div className={classes.breadcrumbViewContainer}>
      <>
        {React.Children.only(children)}
        <IconButton onClick={handleClick}>
          <ArrowDropDownIcon />
        </IconButton>
        <Popover
          className={classes.popupContainer}
          open={dropdownOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}>
          <Box>
            <Card>
              <TextField
                className={classes.searchField}
                label="Drivers filter"
                variant="outlined"
                margin="dense"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  debounceSetSearchText(e.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {loading && (
                        <CircularProgress
                          style={{ width: "32px", height: "32px" }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}></TextField>
              <Divider className={classes.marginBottom4} />
            </Card>
            <Card
              className={classnames(
                classes.dropdownResults,
                "custom-scrollbar"
              )}>
              {driversList.length ? (
                <List dense>
                  {driversList.map((x) => (
                    <ListItem
                      key={x.id}
                      button
                      onClick={() => handleDriverSelected(x)}>
                      <ListItemText
                        primary={
                          <Box display="flex" flexDirection="row" gridGap={5}>
                            <sup>{x.driverId}</sup>
                            <Typography variant="body1">
                              {x.fullName}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography className={classes.dropdownTypography}>
                  No drivers found.
                </Typography>
              )}
            </Card>
          </Box>
        </Popover>
      </>
    </div>
  );
};

export const DriverNameBreadcrumb = ({
  driver,
  getLink,
  getBasePath,
}: Props) => {
  const [selectedDriver, setSelectedDriver] = useState<SelectedDriver>();

  useEffect(() => {
    if (driver) {
      setSelectedDriver(driver);
    }
  }, [driver]);

  if (!selectedDriver) {
    return null;
  }

  const link = getLink ? getLink(selectedDriver) : undefined;
  const driverFullName = selectedDriver?.fullName ?? "";
  const driverId = selectedDriver?.driverId ?? "";
  if (link) {
    return (
      <>
        <DriverNameBreadcrumbView
          getBasePath={getBasePath}
          selectedDriver={selectedDriver}>
          <Link to={link}>
            <DriverNameDisplay fullName={driverFullName} driverId={driverId} />
          </Link>
        </DriverNameBreadcrumbView>
      </>
    );
  }

  return (
    <DriverNameBreadcrumbView
      getBasePath={getBasePath}
      selectedDriver={selectedDriver}>
      <DriverNameDisplay fullName={driverFullName} driverId={driverId} />
    </DriverNameBreadcrumbView>
  );
};
